// Work order photos shown in folders (Arrival / Diagnostic / After), a full-screen viewer with arrows,
// and Before / After pairs per job. Used in the office, on the mechanic's phone and on the customer portal.
import { useEffect, useState, type ReactNode } from 'react';
import { type Row } from './lib';
import { Modal } from './ui';

export const FOLDERS: { id: string; label: string; icon: string; hint: string }[] = [
  { id: 'arrival', label: 'Arrival', icon: '📥', hint: 'How the truck came in' },
  { id: 'diag', label: 'Diagnostic', icon: '🔍', hint: 'What we found' },
  { id: 'after', label: 'After repair', icon: '✅', hint: 'The finished work' },
  { id: '', label: 'Other', icon: '📷', hint: 'More photos' },
];
export const folderOf = (p: Row) => (['arrival', 'diag', 'after'].includes(p.folder) ? p.folder : '');
export const folderLabel = (id: string) => FOLDERS.find(f => f.id === id)?.label || 'Other';

/** Full-screen photo viewer: ← → (keys or buttons), tap outside / Esc to close. */
export function Lightbox({ photos, start, src, onClose, extra }: { photos: Row[]; start: number; src: (id: string) => string; onClose: () => void; extra?: (p: Row) => ReactNode }) {
  const [i, setI] = useState(start);
  const p = photos[i];
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setI(x => Math.min(photos.length - 1, x + 1));
      if (e.key === 'ArrowLeft') setI(x => Math.max(0, x - 1));
    };
    window.addEventListener('keydown', on); return () => window.removeEventListener('keydown', on);
  }, [photos.length, onClose]);
  if (!p) return null;
  return (
    <div className="overlay lightbox pv-box" onClick={onClose} role="dialog" aria-label="Photo">
      <button type="button" className="pv-close" aria-label="Close" onClick={onClose}>×</button>
      {i > 0 && <button type="button" className="pv-nav prev" aria-label="Previous photo" onClick={e => { e.stopPropagation(); setI(i - 1); }}>‹</button>}
      <img src={src(p.id)} alt={p.note || 'Photo'} onClick={e => e.stopPropagation()} />
      {i < photos.length - 1 && <button type="button" className="pv-nav next" aria-label="Next photo" onClick={e => { e.stopPropagation(); setI(i + 1); }}>›</button>}
      <div className="lightbox-cap" onClick={e => e.stopPropagation()}>
        <b>{folderLabel(folderOf(p))}{p.job_title ? ` · ${p.job_title}` : ''}</b>{p.note ? ` — ${p.note}` : ''}
        <small>{p.stamp || new Date(p.created_at).toLocaleString()}{p.uploaded_by_name ? ` · ${p.uploaded_by_name}` : ''} · {i + 1} / {photos.length}</small>
        {extra?.(p)}
      </div>
    </div>
  );
}

/** One card per folder with a few thumbnails and the count. */
export function FolderCards({ photos, src, onOpen, badge }: { photos: Row[]; src: (id: string) => string; onOpen: (folder: string) => void; badge?: (list: Row[]) => ReactNode }) {
  const groups = FOLDERS.map(f => ({ ...f, list: photos.filter(p => folderOf(p) === f.id) })).filter(g => g.list.length || g.id !== '');
  return (
    <div className="pf-cards">
      {groups.map(g => (
        <button key={g.id || 'other'} type="button" className={'pf-card' + (g.list.length ? '' : ' empty')} onClick={() => onOpen(g.id)} disabled={!g.list.length && !badge}>
          <span className="pf-head"><span className="pf-ic">{g.icon}</span><b>{g.label}</b><span className="pf-n">{g.list.length}</span></span>
          <span className="pf-thumbs">{g.list.slice(0, 4).map(p => <img key={p.id} src={src(p.id)} alt="" loading="lazy" />)}
            {!g.list.length && <small className="muted">{g.hint} — none yet</small>}</span>
          {badge?.(g.list)}
        </button>
      ))}
    </div>
  );
}

/** Jobs with both diagnostic (before) and after photos: side by side. */
export function BeforeAfter({ photos, src, onOpen }: { photos: Row[]; src: (id: string) => string; onOpen: (list: Row[], i: number) => void }) {
  const jobs = new Map<string, { title: string; before: Row[]; after: Row[] }>();
  for (const p of photos) {
    if (!p.job_id || !['diag', 'after'].includes(p.folder)) continue;
    const j = jobs.get(p.job_id) || { title: p.job_title || 'Repair', before: [], after: [] };
    (p.folder === 'diag' ? j.before : j.after).push(p);
    jobs.set(p.job_id, j);
  }
  const pairs = [...jobs.values()].filter(j => j.before.length && j.after.length);
  if (!pairs.length) return null;
  return (
    <div className="ba-list">
      {pairs.map(j => {
        const all = [...j.before, ...j.after];
        return (
          <div key={j.title + all[0].id} className="ba-row">
            <b className="ba-title">{j.title}</b>
            <div className="ba-pair">
              <figure><figcaption>Before</figcaption><button type="button" onClick={() => onOpen(all, 0)}><img src={src(j.before[0].id)} alt="Before" loading="lazy" /></button>{j.before.length > 1 && <small>+{j.before.length - 1} more</small>}</figure>
              <figure><figcaption>After</figcaption><button type="button" onClick={() => onOpen(all, j.before.length)}><img src={src(j.after[0].id)} alt="After" loading="lazy" /></button>{j.after.length > 1 && <small>+{j.after.length - 1} more</small>}</figure>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Customer portal: the photos the shop shared, in folders, plus before / after. */
export function SharedPhotos({ photos, src }: { photos: Row[]; src: (id: string) => string }) {
  const [view, setView] = useState<{ list: Row[]; i: number } | null>(null);
  const [folder, setFolder] = useState<string | null>(null);
  if (!photos?.length) return null;
  const inFolder = folder === null ? [] : photos.filter(p => folderOf(p) === folder);
  return (
    <div className="shared-photos no-print">
      <h3>Photos ({photos.length})</h3>
      <BeforeAfter photos={photos} src={src} onOpen={(list, i) => setView({ list, i })} />
      <FolderCards photos={photos} src={src} onOpen={f => setFolder(f)} />
      {folder !== null && !view && (
        <Modal title={`${FOLDERS.find(f => f.id === folder)?.icon || ''} ${folderLabel(folder)} (${inFolder.length})`} onClose={() => setFolder(null)} wide>
          <div className="pf-grid">{inFolder.map((p, i) => <button key={p.id} type="button" className="pf-tile" onClick={() => setView({ list: inFolder, i })}>
            <img src={src(p.id)} alt={p.note || ''} loading="lazy" />{(p.shot || p.job_title) && <span className="pf-tag">{p.shot || p.job_title}</span>}</button>)}</div>
        </Modal>
      )}
      {view && <Lightbox photos={view.list} start={view.i} src={src} onClose={() => setView(null)} />}
    </div>
  );
}
